/**
 * storage-adapter.js — WebMenu storage provider (agnostic).
 *
 * Picks the best persistent backend for the current context:
 *   file:// (desktop HTML)  → localStorage (survives browser close)
 *   http/https (served)     → localStorage (survives browser close)
 *   fallback chain          → localStorage → sessionStorage → memory
 *
 * API: WebMenuStorage.create(namespace) → { get, set, remove, backend }
 */
(function (global) {
  'use strict';

  var memoryBuckets = {};

  function probe(store) {
    try {
      var k = '__wm_probe__';
      store.setItem(k, '1');
      store.removeItem(k);
      return true;
    } catch (e) {
      return false;
    }
  }

  function pickBackend() {
    var isFile = global.location && global.location.protocol === 'file:';

    if (probe(global.localStorage)) {
      return { type: 'localStorage', store: global.localStorage, reason: isFile ? 'file-protocol' : 'served' };
    }
    if (probe(global.sessionStorage)) {
      return { type: 'sessionStorage', store: global.sessionStorage, reason: 'localStorage-unavailable' };
    }
    return { type: 'memory', store: null, reason: 'no-web-storage' };
  }

  function create(namespace) {
    var backend = pickBackend();
    var prefix = (namespace || 'webmenu') + ':';

    function memGet(key) {
      return memoryBuckets[prefix + key] ?? null;
    }
    function memSet(key, val) {
      memoryBuckets[prefix + key] = val;
    }
    function memRemove(key) {
      delete memoryBuckets[prefix + key];
    }

    return {
      backend: backend.type,
      backendReason: backend.reason,

      get: function (key) {
        if (backend.type === 'memory') return memGet(key);
        try {
          return backend.store.getItem(prefix + key);
        } catch (e) {
          return memGet(key);
        }
      },

      set: function (key, value) {
        if (backend.type === 'memory') {
          memSet(key, value);
          return;
        }
        try {
          backend.store.setItem(prefix + key, value);
        } catch (e) {
          memSet(key, value);
        }
      },

      remove: function (key) {
        if (backend.type === 'memory') {
          memRemove(key);
          return;
        }
        try {
          backend.store.removeItem(prefix + key);
        } catch (e) {
          memRemove(key);
        }
      }
    };
  }

  global.WebMenuStorage = { create: create, pickBackend: pickBackend };
})(window);
